<template>
  <Head :title="__('vip-order-summary.meta.title')">
    <meta
      name="description"
      :content="__('vip-order-summary.meta.description')"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.vips.order.summary', [vip, shoutOut])"
    >
    <meta
      property="og:image"
      content="/frontend/assets/favicon/og-image.jpg"
    >
  </Head>

  <Order
    :vip="vip"
    :failed="failed"
  >
    <template #step>
      <div class="w-full md:w-7/12 mt-4 lg:mt-8 mx-auto">
        <div class="border-b border-gray-100 pb-4 lg:border-none">
          <span class="block text-lg font-bold">
            {{ __('vip-order-summary.who-are-you.title') }}
          </span>

          <div class="flex flex-wrap mt-1 text-gray-500">
            <div class="w-1/2">
              {{ __('vip-order-summary.who-are-you.name') }}
            </div>
            <div class="w-1/2 text-right">
              {{ customer.name }}
            </div>
          </div>

          <div class="flex flex-wrap mt-1">
            <div class="w-1/2">
              {{ __('vip-order-summary.who-are-you.email') }}
            </div>
            <div class="w-1/2 text-right">
              {{ customer.email }}
            </div>
          </div>

          <div class="flex flex-wrap mt-1 text-gray-500">
            <div class="w-1/2">
              {{ __('vip-order-summary.who-are-you.phone') }}
            </div>
            <div class="w-1/2 text-right">
              {{ customer.phone ?? '-' }}
            </div>
          </div>
        </div>

        <span class="block text-lg font-bold mt-4">
          {{ __('vip-order-summary.payment.title') }}
        </span>

        <div class="flex flex-wrap mt-1 text-gray-500">
          <div class="w-1/2">
            {{ __('vip-order-summary.payment.amount') }}
          </div>
          <div
            v-if="shoutOut.original_price !== shoutOut.price"
            class="w-1/2 text-right"
          >
            <s>&euro; {{ formatPrice(shoutOut.original_price) }}</s> &euro; {{ formatPrice(shoutOut.price) }}
          </div>
          <div
            v-else
            class="w-1/2 text-right"
          >
            &euro; {{ formatPrice(shoutOut.price) }}
          </div>
        </div>

        <div
          v-if="shoutOut.fast_delivery_fee || shoutOut.visibility_price || shoutOut.no_logo"
          class="flex flex-wrap mt-1 text-gray-500"
        >
          <div class="w-1/2">
            {{ __('vip-order-summary.payment.service-fee') }}
            <div class="relative inline-block has-tooltip w-[15px] h-[15px] mr-1 pt-1">
              <span class="tooltip relative left-5 rounded shadow-lg p-3 bg-white w-[230px] font-normal">
                <div>
                  {{ __('vip-order-summary.payment.service-fee') }}: &euro;{{ formatPrice(shoutOut.service_fee) }}
                </div>
                <div v-show="shoutOut.fast_delivery_fee">
                  {{ __('vip-order-summary.payment.fast-delivery-fee') }}: &euro;{{ formatPrice(shoutOut.fast_delivery_fee) }}<br>
                </div>
                <div v-show="shoutOut.visibility_price">
                  {{ __('vip-order-summary.payment.make-private-video') }}: &euro;{{ formatPrice(shoutOut.visibility_price) }}
                </div>
                <div v-show="shoutOut.no_logo">
                  {{ __('vip-order-summary.payment.no-logo') }}: &euro;{{ formatPrice(750) }}
                </div>
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
              ><path d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24V250.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1H222.6c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" /></svg>
            </div>
          </div>
          <div class="w-1/2 text-right">
            &euro; {{ formatPrice(serviceFee) }}
          </div>
        </div>

        <div
          v-else
          class="flex flex-wrap mt-1 text-gray-500"
        >
          <div class="w-1/2">
            {{ __('vip-order-summary.payment.service-fee') }}
          </div>
          <div class="w-1/2 text-right">
            &euro; {{ formatPrice(shoutOut.service_fee) }}
          </div>
        </div>
      </div>

      <form @submit.prevent="submit">
        <div class="mt-12 w-full lg:text-center">
          <button
            type="submit"
            class="button-green shadow-button-black w-full block lg:w-auto lg:inline-block lg:px-16"
          >
            {{ __('vip-order-summary.continue', {amount: `&euro; ${formatPrice(shoutOut.total_price)}`}) }}
            <img
              src="/frontend/assets/white-arrow.svg"
              class="inline ml-2"
            >
          </button>
        </div>
      </form>
    </template>
  </Order>
</template>

<script>
import Clean from '../../../Layout/Clean.vue';
import Order from './Order.vue';
import GoogleTagManager from '../../../Mixins/GoogleTagManager';

export default {
  components: {
    Order,
  },

  mixins: [GoogleTagManager],

  layout: Clean,

  props: {
    vip: {
      type: Object,
      required: true,
    },

    shoutOut: {
      type: Object,
      required: true,
    },

    customer: {
      type: Object,
      required: true,
    },

    failed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      serviceFee: this.shoutOut.fast_delivery_fee
        + this.shoutOut.service_fee
        + (this.shoutOut.visibility_price ? this.shoutOut.visibility_price : 0)
        + (this.shoutOut.no_logo ? 750 : 0),
    };
  },

  methods: {
    submit() {
      this.gtmBeginCheckout(this.vip);

      this.$inertia.post(this.localeRoute('frontend.vips.order.store', [this.vip, this.shoutOut]));
    },

    formatPrice(price) {
      return (price / 100) % 1 !== 0 ? (price / 100).toFixed(2) : (price / 100).toFixed(0);
    },
  },
};
</script>
